import { gql } from '@apollo/client';
import React, { useEffect } from 'react';
import LocalizedLink from '../../LocalizedLink';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import Image from '../../Image';
import { Sizes } from '../../../constants/Image';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import Price, { PriceType } from '../../Price/Price';
import DiscountPercentage from '../../Price/DiscountPercentage';
import FavouriteIcon from '../../Favourite/FavouriteIcon';
import { useQuery } from '@apollo/client/react';
import useStores from '../../../hooks/useStores';

type Props = {
  category: string;
  hasTopProducts?: (hasProducts: boolean) => void;
};

const TOP_PRODUCTS = gql`
  query TrendingProducts($slug: String, $limit: Int) {
    trendingProducts(slug: $slug, limit: $limit) {
      isNew
      productCode
      slug
      brands {
        id
        title
      }
      cover {
        id
        urls {
          W800
        }
      }
      gridTitle
      title
      prices {
        type
        value
      }
    }
  }
`;

type TrendingProductItem = {
  isNew: boolean;
  productCode: string;
  slug: string;
  brands: { id: string; title: string }[];
  cover: { id: number; urls: { W800: string } };
  gridTitle?: string;
  title: string;
  prices: {
    value: number;
    type: PriceType;
  }[];
};

export default function TopProducts({ category, hasTopProducts }: Props) {
  const { authentication } = useStores();
  const isAuthenticated = authentication?.loggedIn || false;

  const { data, loading, error } = useQuery<{
    trendingProducts: TrendingProductItem[];
  }>(TOP_PRODUCTS, {
    variables: { slug: category, limit: 8 },
    skip: !category,
  });

  useEffect(() => {
    if (!hasTopProducts) return;

    if (!category) {
      hasTopProducts(false);
      return;
    }

    if (error) {
      hasTopProducts(false);
      return;
    }

    if (!loading && data) {
      hasTopProducts((data.trendingProducts?.length ?? 0) > 0);
    }
  }, [category, data, error, loading, hasTopProducts]);

  if (!data || !data.trendingProducts || data.trendingProducts.length === 0) {
    return null;
  }

  const products = data?.trendingProducts;

  const getFilteredPrices = (prices: { value: number; type: PriceType }[]) => {
    if (isAuthenticated) {
      return prices;
    }
    // Filter out client prices when not authenticated
    return prices.filter(
      (price) =>
        price.type !== PriceType.Client && price.type !== PriceType.ClientExtra,
    );
  };

  return (
    <div className="my-16 xl:my-18">
      <h3 className="text-2xl leading-10 font-axi-bold">Top tooted</h3>

      <div className="relative pt-6">
        <Swiper
          modules={[Navigation, Pagination]}
          spaceBetween={16}
          slidesPerView={'auto'}
          pagination={{
            el: '.top-products-swiper-pagination',
            clickable: true,
          }}
          navigation={{
            nextEl: '.top-products-swiper-button-next',
            prevEl: '.top-products-swiper-button-prev',
          }}
          loop={false}
        >
          {products?.map((product) => {
            const filteredPrices = getFilteredPrices(product.prices);
            return (
              <SwiperSlide
                key={product.productCode}
                className="w-72! h-72 shrink-0"
              >
                <LocalizedLink
                  to={`/toode/${product.slug}`}
                  className="group shrink-0 relative block w-72 h-72 overflow-hidden"
                >
                  <Image
                    className="bg-rgrey-lightlight object-cover h-full w-full"
                    image={product.cover}
                    defaultSize={Sizes.W800}
                    sizes={[
                      '(max-width: 1024px) 50vw',
                      '(max-width: 1200px) 30vw',
                      '23vw',
                    ]}
                    allSizes={[Sizes.W300, Sizes.W540, Sizes.W800]}
                    alt={`${product.brands[0].title} ${product.title}`}
                  />
                </LocalizedLink>
                <FavouriteIcon
                  productSlug={product.slug}
                  componentClassName="absolute top-3 left-3 rounded-full bg-rgrey-middle"
                />
                <DiscountPercentage
                  prices={filteredPrices}
                  productNew={product.isNew}
                  componentClassName="absolute right-0"
                  containerClassName="items-end pt-4 pr-2 sm:pr-3"
                  hidePercentage={!isAuthenticated}
                />

                <LocalizedLink to={`/toode/${product.slug}`}>
                  <div className="flex flex-wrap px-1 pt-3 pb-0 md:pt-4 md:pb-2">
                    <div className="w-full pr-1">
                      <h5 className="text-xs text-rgrey md:text-sm font-axi-bold">
                        {`${product.brands[0].title} ${product.title}`}
                      </h5>
                      <h5 className="text-sm font-axi-bold">{product.title}</h5>
                    </div>
                    <div className="mt-1">
                      <Price
                        priceLabel={true}
                        prices={filteredPrices}
                        productNew={product.isNew}
                        discountPriceClassname="text-base mr-1"
                        defaultPriceClassName="text-sm"
                        discountLabelClassname="text-xs"
                      />
                    </div>
                  </div>
                </LocalizedLink>
              </SwiperSlide>
            );
          })}
        </Swiper>
        <div className="flex justify-start items-center gap-x-4 mt-12 -ml-1">
          <button
            aria-label="eelmine"
            className="swiper-button-prev top-products-swiper-button-prev"
          />

          <div className="swiper-pagination top-products-swiper-pagination" />
          <button
            aria-label="järgmine"
            className="swiper-button-next top-products-swiper-button-next"
          />
        </div>
      </div>
    </div>
  );
}
